import React from "react";
import "@/App.css";
import { ReactLenis } from "lenis/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider, LangRouteSync } from "@/i18n";
import HomePage from "@/pages/HomePage";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import AGB from "@/pages/AGB";
import NotFound from "@/pages/NotFound";
import { ScrollToTop } from "@/components/site/ScrollToTop";
import { OfferPopup } from "@/components/site/OfferPopup";
import CookieConsent from "@/components/site/CookieConsent";
import { RouteErrorBoundary } from "@/components/site/ErrorBoundary";
import { SrpErrorBoundary } from "@/srp/SrpErrorBoundary";
import ServicesPage from "@/pages/ServicesPage";
import { lazyRoute, SRP_FALLBACK, ROUTE_FALLBACK } from "@/lib/lazyRoute";

// The SRP assistant is heavy (temporal polyfill, report engine) — it stays
// code-split, but with an imperative preload that index.js awaits before
// hydrating a prerendered /srp page, so no Suspense boundary crosses
// hydration. ServicesPage is light (framer-motion/lucide only) and eager.
const SrpAssistant = lazyRoute(() => import("@/pages/SrpAssistant"), SRP_FALLBACK);
const SrpReview = lazyRoute(() => import("@/pages/SrpReview"), SRP_FALLBACK);

// Heavy content routes are code-split; each registers a preload so hydration
// of its prerendered HTML runs with the chunk already loaded (no Suspense
// boundary crossing hydration, no fallback flash).
const InsightsIndex = lazyRoute(() => import("@/pages/InsightsIndex"), ROUTE_FALLBACK);
const InsightsDetail = lazyRoute(() => import("@/pages/InsightsDetail"), ROUTE_FALLBACK);
const Toolkit = lazyRoute(() => import("@/pages/Toolkit"), ROUTE_FALLBACK);
const ToolDetail = lazyRoute(() => import("@/pages/ToolDetail"), ROUTE_FALLBACK);
const CraTopicHub = lazyRoute(() => import("@/pages/CraTopicHub"), ROUTE_FALLBACK);
const NewsletterArchive = lazyRoute(() => import("@/pages/NewsletterArchive").then((m) => ({ default: m.NewsletterArchive })), ROUTE_FALLBACK);
const NewsletterIssue = lazyRoute(() => import("@/pages/NewsletterArchive").then((m) => ({ default: m.NewsletterIssue })), ROUTE_FALLBACK);

// Awaited by index.js before hydrateRoot when the current URL matches.
export function preloadForPath(pathname) {
  if (pathname === "/srp") return SrpAssistant.load();
  if (pathname === "/srp/review") return SrpReview.load();
  if (pathname === "/insights") return InsightsIndex.load();
  if (pathname === "/insights/toolkit") return Toolkit.load();
  if (pathname.startsWith("/insights/toolkit/")) return ToolDetail.load();
  if (pathname === "/insights/newsletter") return NewsletterArchive.load();
  if (pathname.startsWith("/insights/newsletter/")) return NewsletterIssue.load();
  if (pathname === "/cra") return CraTopicHub.load();
  if (pathname.startsWith("/insights/")) return InsightsDetail.load();
  return Promise.resolve();
}

const withRouteErrorBoundary = (element) => (
  <RouteErrorBoundary>{element}</RouteErrorBoundary>
);

function App() {
  return (
    <I18nProvider>
      {/*
        ReactLenis MUST wrap the entire routed tree — otherwise the `useLenis`
        hook returns undefined for every descendant (Header, Hero, ScrollToTop
        etc.). When that happens, all navigation scroll calls fall back to
        native `scrollIntoView`, which desyncs Lenis' internal transform from
        `documentElement.scrollTop` — the classic symptom is a completely
        BLACK page after clicking the CRA-Check nav or landing on
        `nextech.law/#assessment` on a cold load.
      */}
      <ReactLenis root options={{ duration: 1.1, smoothWheel: true }}>
        <BrowserRouter>
          <ScrollToTop />
          <LangRouteSync />
          <MotionConfig reducedMotion="user">
          <div className="App noise-overlay bg-[#030712] text-white min-h-screen relative">
            <Routes>
              <Route path="/" element={withRouteErrorBoundary(<HomePage />)} />
              <Route path="/impressum" element={withRouteErrorBoundary(<Impressum />)} />
              <Route path="/datenschutz" element={withRouteErrorBoundary(<Datenschutz />)} />
              <Route path="/agb" element={withRouteErrorBoundary(<AGB />)} />
              <Route path="/insights" element={withRouteErrorBoundary(<InsightsIndex />)} />
              <Route path="/insights/toolkit" element={withRouteErrorBoundary(<Toolkit />)} />
              <Route path="/insights/toolkit/:slug" element={withRouteErrorBoundary(<ToolDetail />)} />
              <Route path="/insights/newsletter" element={withRouteErrorBoundary(<NewsletterArchive />)} />
              <Route path="/insights/newsletter/:slug" element={withRouteErrorBoundary(<NewsletterIssue />)} />
              <Route path="/services" element={withRouteErrorBoundary(<ServicesPage />)} />
              <Route path="/en/services" element={withRouteErrorBoundary(<ServicesPage />)} />
              {/* Temporary 302-equivalent client redirect. The hosting layer
                 (preview middleware / production edge) answers with a real
                 HTTP 302 before this app code ever runs; this client fallback
                 only covers static-hosting scenarios without redirect rules. */}
              <Route
                path="/cra-control-room"
                element={<Navigate to={{ pathname: "/services", hash: "#cra-control-room" }} replace />}
              />
              <Route path="/cra" element={withRouteErrorBoundary(<CraTopicHub />)} />
              <Route
                path="/srp"
                element={withRouteErrorBoundary(
                  <SrpErrorBoundary>
                    <SrpAssistant />
                  </SrpErrorBoundary>
                )}
              />
              <Route
                path="/srp/review"
                element={withRouteErrorBoundary(
                  <SrpErrorBoundary>
                    <SrpReview />
                  </SrpErrorBoundary>
                )}
              />
              <Route path="/insights/:slug" element={withRouteErrorBoundary(<InsightsDetail />)} />
              <Route path="*" element={withRouteErrorBoundary(<NotFound />)} />
            </Routes>
            <Toaster position="bottom-right" theme="dark" />
            <OfferPopup />
            <CookieConsent />
          </div>
          </MotionConfig>
        </BrowserRouter>
      </ReactLenis>
    </I18nProvider>
  );
}

export default App;
